import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ParkingSquare, Sparkles, Lock } from "lucide-react";
import LocationPicker from "../components/LocationPicker";
import VehiclePlacer from "../components/VehiclePlacer";
import AppealAnalysisCard from "../components/AppealAnalysisCard";
import { citationLabel, citationStyle, confidenceClass, confidenceLabel, sourceCategory } from "../lib/citations";
import { useAuth } from "../lib/auth";

export default function ParkingAssistant() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    const [settings, setSettings] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [mapBlobUrl, setMapBlobUrl] = useState(null);
    const [vehiclePlacement, setVehiclePlacement] = useState(null);
    const [form, setForm] = useState({
        pcn_reference: "",
        issue_date: "",
        location: "",
        location_id: "",
        vehicle_reg: "",
        alleged_contravention: "",
        issuing_authority: "",
        additional_notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [selectedGroundIds, setSelectedGroundIds] = useState([]);
    const [draft, setDraft] = useState(null);

    useEffect(() => {
        api.get("/estate/settings")
            .then((res) => {
                setSettings(res.data);
                setForm((f) => ({ ...f, issuing_authority: res.data.issuing_agency_name }));
            })
            .catch(() => {});
    }, []);

    const next = () => setStep((s) => Math.min(4, s + 1));
    const prev = () => setStep((s) => Math.max(1, s - 1));

    const onLocationChange = (loc, suggestion) => {
        setSelectedLocation(loc);
        setForm((f) => ({
            ...f,
            location_id: loc?.id || "",
            location: loc?.label || suggestion?.label || "",
            additional_notes: suggestion
                ? `${f.additional_notes || ""}${f.additional_notes ? "\n" : ""}[Pending location suggestion] ${suggestion.label}${suggestion.description ? ` - ${suggestion.description}` : ""}`.trim()
                : f.additional_notes,
        }));
    };

    const runAnalysis = async () => {
        setAnalyzing(true);
        try {
            const intake = { ...form };
            if (vehiclePlacement) intake.vehicle_placement = vehiclePlacement;
            const res = await api.post("/parking/analyze", intake);
            setAnalysis(res.data);
            // Default selection: pre-tick grounds with high/medium strength
            const preselect = (res.data.grounds || [])
                .filter((g) => g.strength !== "low")
                .map((g) => g.id);
            setSelectedGroundIds(preselect.length ? preselect : (res.data.grounds || []).map((g) => g.id));
            setStep(3);
            toast.success("Analysis complete");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not analyse appeal");
        } finally {
            setAnalyzing(false);
        }
    };

    const toggleGround = (id) => {
        setSelectedGroundIds((ids) =>
            ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
        );
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const intake = { ...form };
            if (vehiclePlacement) intake.vehicle_placement = vehiclePlacement;
            const body = {
                intake,
                selected_ground_ids: selectedGroundIds,
                analysis: analysis,
            };
            const res = await api.post("/parking/assist", body);
            setDraft(res.data);
            setStep(4);
            toast.success("Appeal draft generated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not generate appeal");
        } finally {
            setSubmitting(false);
        }
    };

    const copyAdminRequest = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Admin request copied");
        } catch {
            toast.info(text);
        }
    };

    const handleChecklistAction = (item) => {
        const target = item.action_target || {};
        if (item.action_type === "form_step") {
            setStep(target.step || 1);
            return;
        }

        if (item.action_type === "documents_upload") {
            const params = new URLSearchParams({
                upload: "1",
                scope: target.scope || "private",
                title: target.title || "",
                description: target.description || "",
            });
            navigate(`/documents?${params.toString()}`);
            return;
        }

        if (item.action_type === "admin_kb_request") {
            if (user?.role === "estate_admin") {
                const params = new URLSearchParams({
                    upload: "1",
                    scope: target.scope || "estate_kb",
                    kb_category: target.kb_category || "governing",
                    title: target.title || "",
                    description: target.request || "",
                });
                navigate(`/documents?${params.toString()}`);
            } else {
                copyAdminRequest(target.request || item.action || "Please upload the missing estate knowledge-base document.");
            }
        }
    };

    const copyLetter = async () => {
        if (!draft?.appeal_letter) return;
        await navigator.clipboard.writeText(draft.appeal_letter);
        toast.success("Letter copied to clipboard");
    };

    const downloadPack = async () => {
        if (!draft?.id) return;
        try {
            const res = await api.get(`/parking/drafts/${draft.id}/evidence-pack`, {
                params: { include_signature: false },
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `evidence-pack-${draft.intake?.pcn_reference || "pcn"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Evidence pack downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not generate evidence pack");
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="parking-assistant-page">
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    <ParkingSquare size={14} /> Parking Ticket Assistant
                </div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Draft a PCN appeal</h1>
                <p className="text-sm text-slate-500 mt-1">
                    We retrieve relevant estate rules and your private evidence, then draft a polite, formal appeal letter for you.
                </p>
            </div>

            <ol className="flex items-center gap-3 text-xs uppercase tracking-widest text-slate-500 flex-wrap" data-testid="assistant-steps">
                <li className={step >= 1 ? "text-slate-900 font-semibold" : ""}>1. Ticket details</li>
                <li>›</li>
                <li className={step >= 2 ? "text-slate-900 font-semibold" : ""}>2. Context</li>
                <li>›</li>
                <li className={step >= 3 ? "text-slate-900 font-semibold" : ""}>3. Analyse</li>
                <li>›</li>
                <li className={step >= 4 ? "text-slate-900 font-semibold" : ""}>4. Draft letter</li>
            </ol>

            {step === 1 && (
                <section className="border border-slate-200 bg-white rounded-lg p-5 space-y-5" data-testid="step-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="pcn">PCN reference</Label>
                            <Input id="pcn" value={form.pcn_reference} onChange={(e) => setForm({ ...form, pcn_reference: e.target.value })} placeholder="e.g. PCN-2026-0023" required data-testid="pcn-ref-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="date">Issue date</Label>
                            <Input id="date" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} required data-testid="pcn-date-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="reg">Vehicle registration</Label>
                            <Input id="reg" value={form.vehicle_reg} onChange={(e) => setForm({ ...form, vehicle_reg: e.target.value })} placeholder="ABC123" required data-testid="pcn-vehicle-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="flex items-center gap-1.5"><Lock size={12} /> Issuing authority</Label>
                            <Input
                                value={form.issuing_authority}
                                readOnly
                                disabled
                                className="bg-slate-50 text-slate-600"
                                data-testid="pcn-authority-input"
                            />
                            <div className="text-xs text-slate-500">
                                The estate uses a single parking management contractor.
                                {settings?.issuing_agency_contact && (
                                    <> Contact: <span className="text-slate-700">{settings.issuing_agency_contact}</span></>
                                )}
                            </div>
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                            <Label htmlFor="cont">Alleged contravention</Label>
                            <Textarea id="cont" rows={3} value={form.alleged_contravention} onChange={(e) => setForm({ ...form, alleged_contravention: e.target.value })} placeholder="e.g. Parked without a valid permit" required data-testid="pcn-contravention-input" />
                        </div>
                    </div>

                    <div className="border-t border-slate-200 pt-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Pinpoint the location</div>
                        <LocationPicker
                            value={selectedLocation}
                            onSelect={onLocationChange}
                            onMapReady={setMapBlobUrl}
                        />
                    </div>

                    {selectedLocation && mapBlobUrl && (
                        <div className="border-t border-slate-200 pt-5">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Place your vehicle (optional)</div>
                            <p className="text-sm text-slate-500 mb-3">Drag the car onto your exact parking position, rotate it to match how you were parked, and resize it to your vehicle&apos;s footprint.</p>
                            <VehiclePlacer
                                mapBlobUrl={mapBlobUrl}
                                anchor={selectedLocation}
                                value={vehiclePlacement}
                                onChange={setVehiclePlacement}
                            />
                        </div>
                    )}

                    <div className="flex justify-end">
                        <Button
                            onClick={next}
                            className="bg-slate-700 hover:bg-slate-800 text-white"
                            data-testid="step-1-next"
                            disabled={!form.pcn_reference || !form.issue_date || !form.vehicle_reg || !form.alleged_contravention || !form.location}
                        >
                            Next: Context
                        </Button>
                    </div>
                </section>
            )}

            {step === 2 && (
                <section className="border border-slate-200 bg-white rounded-lg p-5 space-y-4" data-testid="step-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="notes">Additional notes (optional)</Label>
                        <Textarea
                            id="notes"
                            rows={5}
                            value={form.additional_notes}
                            onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                            placeholder="Anything else relevant — e.g. you were parked in your own allocated bay, signage was missing, you have a permit from the estate, etc."
                            data-testid="pcn-notes-input"
                        />
                    </div>
                    <div className="text-xs text-slate-500">
                        Tip: upload any supporting evidence (permits, photos, deeds) under <strong>Documents → My documents</strong> first, so we can cite it.
                    </div>
                    <div className="flex justify-between">
                        <Button variant="outline" onClick={prev} data-testid="step-2-back">Back</Button>
                        <Button onClick={runAnalysis} disabled={analyzing} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="step-2-analyse">
                            <Sparkles size={14} className="mr-2" />
                            {analyzing ? "Analysing…" : "Analyse appeal"}
                        </Button>
                    </div>
                </section>
            )}

            {step === 3 && analysis && (
                <div data-testid="step-3">
                    <AppealAnalysisCard
                        analysis={analysis}
                        selectedIds={selectedGroundIds}
                        onToggleGround={toggleGround}
                        onBack={() => setStep(2)}
                        onChecklistAction={handleChecklistAction}
                        onContinue={submit}
                        submitting={submitting}
                    />
                </div>
            )}

            {step === 4 && draft && (
                <section className="space-y-4" data-testid="step-4">
                    {!draft.has_sufficient_evidence && (
                        <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg px-4 py-3 text-sm" data-testid="insufficient-evidence-warning">
                            <strong>Insufficient evidence.</strong> We couldn&apos;t find specific estate rules or private documents that directly support this case. We&apos;ve drafted a formal request-for-information letter instead — consider uploading supporting documents and trying again.
                        </div>
                    )}
                    <div className="border border-slate-200 bg-white rounded-lg p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500">Draft appeal letter</div>
                                <h2 className="font-heading text-lg font-semibold text-slate-900 mt-1">Ready for review</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={copyLetter} data-testid="copy-letter-button">Copy</Button>
                                <Button onClick={downloadPack} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="download-evidence-pack-button">
                                    Download evidence pack (PDF)
                                </Button>
                            </div>
                        </div>
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-body" data-testid="appeal-letter-text">{draft.appeal_letter}</pre>
                    </div>

                    {draft.citations?.length > 0 && (
                        <div className="border border-slate-200 bg-white rounded-lg p-5">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Citations</div>
                            <ul className="space-y-3">
                                {draft.citations.map((c, i) => {
                                    const style = citationStyle(c);
                                    return (
                                        <li key={i} className="text-sm" data-testid={`citation-${i}`}>
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span
                                                    className="inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border"
                                                    style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
                                                >
                                                    {citationLabel(c)}
                                                </span>
                                                <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${confidenceClass(c)}`}>
                                                    {confidenceLabel(c)}
                                                </span>
                                                <span className="text-xs text-slate-500">{sourceCategory(c)}</span>
                                            </div>
                                            {c.document_title && <strong className="text-slate-800">{c.document_title}: </strong>}
                                            <span className="text-slate-700">{c.snippet}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    <div className="flex justify-between">
                        <Button variant="outline" onClick={() => { setStep(1); setDraft(null); setAnalysis(null); setSelectedGroundIds([]); }} data-testid="start-over-button">Start over</Button>
                        <Button onClick={() => navigate(`/cases/${draft.case_id}`)} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="view-case-button">View linked case</Button>
                    </div>
                </section>
            )}
        </div>
    );
}
